import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import AppRoot from "./AppRoot";
import "flag-icons/css/flag-icons.min.css";
import { StandaloneView, parseStandalonePath, resolveStandaloneLanguage } from "./standalone/StandaloneView";
import PartnerTransportListPage from "./tenants/default/PartnerTransportListPage";
import CarrierLoginPage from "./tenants/default/CarrierLoginPage";
import CarrierTransportListPage from "./tenants/default/CarrierTransportListPage";
import { StartupScreen } from "./StartupScreen";
import { API_BASE_URL } from "./config";
import { storeTokens } from "./api/auth";

// Auto-redirect to ?tenant=intecio if no tenant parameter is present (dev mode only)
const urlParams = new URLSearchParams(window.location.search);
if (import.meta.env.DEV && !urlParams.has('tenant') && !urlParams.has('access_token') && window.location.pathname === '/') {
  const newUrl = new URL(window.location.href);
  newUrl.searchParams.set('tenant', 'intecio');
  window.location.replace(newUrl.toString());
}

// CRITICAL: Extract SSO tokens from URL BEFORE any React rendering
// This prevents race conditions where components call fetchSession() before tokens are stored
const accessToken = urlParams.get("access_token");
const refreshToken = urlParams.get("refresh_token");

if (accessToken && refreshToken) {
  storeTokens(accessToken, refreshToken);

  // Set flag to auto-open feed after SSO
  sessionStorage.setItem("sso-auto-open-feed", "true");

  // Clean URL (remove tokens and SSO params for security)
  const currentUrl = window.location.href;
  const cleanedUrl = currentUrl
    .replace(/[?&]access_token=[^&]*/g, "")
    .replace(/[?&]refresh_token=[^&]*/g, "")
    .replace(/[?&]provider=[^&]*/g, "")       // Remove provider param
    .replace(/[?&]auth=[^&]*/g, "")            // Remove auth param
    .replace(/\?&/, "?")  // Fix malformed query string
    .replace(/\?$/, "");   // Remove trailing ?

  if (currentUrl !== cleanedUrl) {
    window.history.replaceState({}, "", cleanedUrl);
  }
}

function RootApp() {
  const [isReady, setIsReady] = useState(false);
  const location = useLocation();
  const { pathname, search } = location;
  const standalone = parseStandalonePath(pathname);

  // Check if we have terminal parameters in URL (bypass startup screen for terminals)
  const urlParams = new URLSearchParams(search);
  const hasTerminalParams = urlParams.has('tenant') && urlParams.has('app');

  // Skip startup screen if we're on SSO complete route
  const isSsoCompleteRoute = pathname === '/auth/sso-complete';

  // Skip startup screen if directly accessing console route
  const isConsoleRoute = pathname === '/console';

  // Check if we're running inside an iframe (app loaded in feed)
  const isInIframe = window.self !== window.top;

  // Check for conditions that skip startup screen
  useEffect(() => {
    // Skip startup screen if directly accessing console
    if (isConsoleRoute) {
      console.log('[main.tsx] Console route detected, skipping startup screen');
      setIsReady(true);
      return;
    }

    // Skip startup screen if we have terminal parameters (terminal auth handled in AIpad)
    if (hasTerminalParams) {
      console.log('[main.tsx] Terminal parameters detected, skipping startup screen');
      setIsReady(true);
      return;
    }

    // Skip startup screen if running in iframe (app loaded in feed)
    if (isInIframe) {
      console.log('[main.tsx] Running in iframe, skipping startup screen');
      setIsReady(true);
      return;
    }

    // Check for SSO auto-open flag (set in main.tsx before React renders)
    const ssoAutoOpenFlag = sessionStorage.getItem('sso-auto-open-feed');
    if (ssoAutoOpenFlag === 'true') {
      // Clear flag immediately after detection
      sessionStorage.removeItem('sso-auto-open-feed');
      setIsReady(true);
      return;
    }

    // If we're on SSO complete route, skip auth check and go straight to AppRoot
    if (isSsoCompleteRoute) {
      setIsReady(true);
      return;
    }
  }, [isSsoCompleteRoute, isInIframe, hasTerminalParams, isConsoleRoute]);

  // Show startup screen for main app routes (not for standalone, partner, or carrier pages)
  const isMainApp = !standalone && pathname !== "/partner-transports" && !pathname.startsWith("/carrier-access/");

  if (isMainApp && !isReady) {
    return <StartupScreen onReady={() => setIsReady(true)} />;
  }

  if (standalone) {
    const language = resolveStandaloneLanguage(search) ?? undefined;
    return <StandaloneView resourceId={standalone.id} language={language} search={search} />;
  }

  // Partner-Seite ohne Auth-Zwang
  if (pathname === "/partner-transports") {
    return <PartnerTransportListPage />;
  }

  // Carrier Access Pages - check transport list FIRST (more specific path)
  if (pathname.match(/^\/carrier-access\/[a-f0-9]{64}\/transports/)) {
    return <CarrierTransportListPage />;
  }

  // Then check login page (less specific, matches base path)
  if (pathname.match(/^\/carrier-access\/[a-f0-9]{64}$/)) {
    return <CarrierLoginPage />;
  }

  // AIpad Framework loads for all other routes
  return <AppRoot />;
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <RootApp />
    </BrowserRouter>
  </React.StrictMode>
);
